import { Path, UseFormReturn } from "react-hook-form"
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber"
import { hankaku, zenkaku } from "./string-ja-utils"

const phoneUtil = PhoneNumberUtil.getInstance()

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const ZENKAKU_ADDRESS_REGEX = new RegExp(
  // eslint-disable-next-line max-len
  "^[\uFF41-\uFF5A\uFF21-\uFF3A\u3041-\u3093\u30A1-\u30F6\uFF10-\uFF19\u30FC-\u9FAF\u3005\uff01-\uff0f\uff1a-\uff20\uff3b-\uff40\uff5b-\uff5e\u3001-\u301c\u201d\u2019\u30fb\u266f\u03B1-\u03C9　]+$"
)

export const getZenkakuFormUtils = <T>(
  form: UseFormReturn<T>,
  {
    enableAutocomplete = true,
  }: {
    enableAutocomplete: boolean
  }
) => {
  const registerAutoFillAsZenkaku = (name: Path<T>) => {
    return form.register(name, {
      pattern: {
        value: ZENKAKU_ADDRESS_REGEX,
        message: "使用できない文字が含まれています。",
      },
      onBlur: (e) => {
        if (enableAutocomplete) {
          e.target.value = zenkaku(e.target.value)
        }
      },
      setValueAs: (value) => {
        return zenkaku(value)
      },
    })
  }

  return {
    registerAutoFillAsZenkaku,
  }
}

const HANKAKU_KANA_ADDRESS_REGEX = /^[a-zA-Zｧ-ﾝﾞﾟ0-9\-･]+$/
const CIC_HANKAKU_KANA_REGEX = /^[ｧ-ﾝﾞﾟ]+$/

export const isCICValidHankakuKana = (str: string) => {
  return str.match(CIC_HANKAKU_KANA_REGEX) !== null
}

export const isHankaku = (str: string): boolean => {
  // 全角文字の範囲をチェック（ひらがな、カタカナ、漢字、全角英数字、全角記号など）
  const zenkakuPattern = /[^\x00-\x7F\uFF61-\uFF9F]/
  return !zenkakuPattern.test(str)
}

export type EmailValidationReason = "empty" | "format"

export type EmailValidationResult = {
  isValid: boolean
  reason: EmailValidationReason | null
}

/**
 * メールアドレス形式の簡易検証を行い、結果と理由を返却。
 */
export const isValidEmail = (value: string): EmailValidationResult => {
  if (typeof value !== "string") {
    return { isValid: false, reason: "format" }
  }

  const trimmed = value.trim()

  if (trimmed === "") {
    return { isValid: false, reason: "empty" }
  }

  if (!EMAIL_REGEX.test(trimmed)) {
    return { isValid: false, reason: "format" }
  }

  return { isValid: true, reason: null }
}

const isValidHankakuKana = (str: string) => {
  return str.match(HANKAKU_KANA_ADDRESS_REGEX) !== null
}

export const getHankakuFormUtils = <T>(
  form: UseFormReturn<T>,
  {
    enableAutocomplete = true,
  }: {
    enableAutocomplete: boolean
  }
) => {
  const registerAutoFillAsHankaku = (name: Path<T>) => {
    return form.register(name, {
      pattern: {
        value: HANKAKU_KANA_ADDRESS_REGEX,
        message: "使用できない文字が含まれています。",
      },
      onBlur: (e) => {
        if (enableAutocomplete) {
          e.target.value = hankaku(e.target.value)
        }
      },
      setValueAs: (value) => {
        return hankaku(value)
      },
    })
  }

  const registerAutoFillAsHankakuNumber = (name: Path<T>) => {
    return form.register(name, {
      onBlur: (e) => {
        if (enableAutocomplete) {
          e.target.value = hankaku(String(e.target.value || ""))
        }
      },
      setValueAs: (value) => {
        // 値を文字列に変換してからhankaku関数を適用
        const stringValue = String(value || "")
        const hankakuValue = hankaku(stringValue)
        // 数値フィールドの場合は数値に変換、空文字の場合はnullを返す
        if (hankakuValue === "" || hankakuValue == null) {
          return null
        }
        const numValue = Number(hankakuValue)
        return isNaN(numValue) ? null : numValue
      },
    })
  }

  const registerPhoneNumberOnly = (name: Path<T>) => {
    return form.register(name, {
      onChange: (e) => {
        if (enableAutocomplete) {
          // 数字のみを残す
          e.target.value = getPhoneNumberOnly(e.target.value || "")
        }
      },
      setValueAs: (value) => {
        // 数字のみを残す
        const stringValue = String(value || "")
        return getPhoneNumberOnly(stringValue)
      },
    })
  }

  return {
    registerAutoFillAsHankaku,
    registerAutoFillAsHankakuNumber,
    registerPhoneNumberOnly,
  }
}

export function isKanjiOrKatakana(str: string) {
  const pattern = /[^\u4E00-\u9FFFァ-ヶー]/
  return !pattern.test(str)
}

export function getPhoneNumberOnly(phoneNumber: string): string {
  if (!phoneNumber) return ""

  try {
    // まず数字のみを抽出（従来の実装と同様）
    const digitsOnly = String(phoneNumber).replace(/[^0-9]/g, "")

    // 日本の電話番号として解析を試行
    const parsedNumber = phoneUtil.parseAndKeepRawInput(digitsOnly, "JP")

    // 有効な番号の場合、国内フォーマット（数字のみ）で返す
    if (phoneUtil.isValidNumber(parsedNumber)) {
      return phoneUtil.format(parsedNumber, PhoneNumberFormat.NATIONAL).replace(/[^0-9]/g, "")
    }

    // 解析に失敗した場合は元の数字のみの文字列を返す
    return digitsOnly
  } catch (error) {
    // エラーの場合は従来の方法で数字のみを返す
    return String(phoneNumber).replace(/[^0-9]/g, "")
  }
}

export function isValidPhoneNumber(phoneNumber: string): boolean {
  // 空文字は有効とする
  if (phoneNumber === "") return true

  try {
    // 日本の電話番号として解析
    const parsedNumber = phoneUtil.parseAndKeepRawInput(phoneNumber, "JP")

    // 有効性を検証
    return phoneUtil.isValidNumber(parsedNumber)
  } catch (error) {
    // 解析エラーの場合は無効とする
    return false
  }
}
